import { useContext, useState, useRef, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import Alert from 'react-bootstrap/Alert'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Badge from 'react-bootstrap/Badge'
import { faArrowLeft, faUndo, faPlus, faUpload, faClipboardCheck, faPen, faFilePdf, faFileAlt, faList } from '@fortawesome/free-solid-svg-icons'
import { usePolicyDocument, useUpdatePolicyDocumentSections, useDownloadPolicyDocument, useUpdateRenderedHtml, useMatchAnalytics } from 'api/hooks/use-policy-documents'
import PolicyUploadModal from './PolicyUploadModal'
import { useAcceptPolicyRule, useUpdatePolicyRule, useDeletePolicyRule, useLinkAnalytic, useUnlinkAnalytic, useBulkAcceptPendingRules, useMatchRuleAnalytics } from 'api/hooks/use-policy-rules'
import { useAnalytics } from 'api/hooks/use-analytics'
import useSections from 'api/hooks/use-sections'
import UserContext from 'context/user-context'
import PolicyRuleCard from './PolicyRuleCard'
import PolicyDocumentContent from './PolicyDocumentContent'

// Sections the composable-analytics Lambda supports — mirrors the admin
// custom-analytics page. Used to offer "Compose Analytic" from a policy rule.
const PolicyItem = ({ policyDocumentId, onBack }) => {
  const user = useContext(UserContext)
  const location = useLocation()
  const aiEnabled = user.municipality?.setting?.ai_search_enabled
  const [viewMode, setViewMode] = useState('document')
  // Bumped to ask the document panel to jump to the first pending rule.
  const [reviewTrigger, setReviewTrigger] = useState(0)
  const startReview = () => {
    setViewMode('document')
    setReviewTrigger((t) => t + 1)
  }
  const autoStartedRef = useRef(false)
  const renderPollCountRef = useRef(0)
  const { data, isLoading, error } = usePolicyDocument({
    policyDocumentId,
    options: {
      refetchInterval: (queryData) => {
        if (queryData?.status === 'extracting' || queryData?.matching_status === 'matching') {
          renderPollCountRef.current = 0
          return 5000
        }
        // The worker flips status to "extracted" before the (slower) HTML render
        // finishes, so keep polling briefly until rendered_html lands.
        const awaitingHtml =
          queryData?.has_document && queryData?.status === 'extracted' && !queryData?.rendered_html
        if (awaitingHtml && renderPollCountRef.current < 24) {
          renderPollCountRef.current += 1
          return 5000
        }
        return false
      }
    }
  })
  const [selectedSectionIds, setSelectedSectionIds] = useState([])
  const [showSectionPicker, setShowSectionPicker] = useState(false)
  const sectionPickerRef = useRef(null)

  // Composable sections the municipality has AND the user is granted (the
  // API rejects a save on any other). Default the compose modal to a section
  // the policy doc applies to, else the first.
  const composableSections = useMemo(() => {
    const userAbbreviations = new Set(user.section_abbreviations || [])
    return (user.municipality_sections || [])
      .filter((ms) => ms.section?.is_composable && userAbbreviations.has(ms.section.abbreviation))
      .map((ms) => ({ id: ms.section.id, name: ms.section.name, abbreviation: ms.section.abbreviation }))
  }, [user.municipality_sections, user.section_abbreviations])
  const defaultComposeSectionId = useMemo(() => {
    const docSectionIds = new Set((data?.sections || []).map((s) => s.id))
    return composableSections.find((s) => docSectionIds.has(s.id))?.id || composableSections[0]?.id
  }, [composableSections, data])

  useEffect(() => {
    const handler = (e) => {
      if (sectionPickerRef.current && !sectionPickerRef.current.contains(e.target)) {
        setShowSectionPicker(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])
  const [showRejectedModal, setShowRejectedModal] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [justUploaded, setJustUploaded] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const [newCategory, setNewCategory] = useState('')
  const [newContent, setNewContent] = useState('')
  const [newSummary, setNewSummary] = useState('')

  const { mutate: createRule, mutateAsync: createRuleAsync, isLoading: isCreatingRule } = useAcceptPolicyRule({
    policyDocumentId,
    options: {
      onSuccess: () => {
        setNewCategory('')
        setNewContent('')
        setNewSummary('')
        setShowAddForm(false)
      }
    }
  })
  const { mutate: updateRule, mutateAsync: updateRuleAsync } = useUpdatePolicyRule({ policyDocumentId })
  const { mutateAsync: bulkAcceptAsync } = useBulkAcceptPendingRules({ policyDocumentId })
  const { mutateAsync: persistHtmlAsync } = useUpdateRenderedHtml({ policyDocumentId })
  const { mutate: deleteRule, mutateAsync: deleteRuleAsync } = useDeletePolicyRule({ policyDocumentId })
  const { mutate: linkAnalytic, mutateAsync: linkAnalyticAsync } = useLinkAnalytic({ policyDocumentId })
  const { mutate: unlinkAnalytic } = useUnlinkAnalytic({ policyDocumentId })
  const { mutateAsync: matchRuleAnalyticsAsync } = useMatchRuleAnalytics({ policyDocumentId })
  // No `core: true` here: AnalyticPolicy::Scope (api/app/policies/analytic_policy.rb)
  // already restricts results to global core analytics + this municipality's own
  // custom analytics, so this fetch is the full set of analytics linkable/composable
  // from this page. Filtering to `core` on top of that would silently drop every
  // custom analytic from AnalyticSearch (manual linking) and from the
  // allAnalytics.find(...) lookups in PolicyDocumentContent/PolicyRuleCard that
  // render a linked custom analytic's description.
  const { data: analyticsData } = useAnalytics({ params: { perPage: 1000 } })
  const { data: sectionsData } = useSections()
  const { mutate: updateSections, isLoading: isSavingSections } = useUpdatePolicyDocumentSections({ policyDocumentId })
  const { mutate: downloadDocument, isLoading: isDownloading } = useDownloadPolicyDocument()
  const { mutate: retryMatching, isLoading: isRetryingMatch } = useMatchAnalytics()

  // All rules with their review status, in document order, for the inline review flow.
  const allRules = useMemo(
    () =>
      [
        ...(data?.pending_rules || []),
        ...(data?.rules || []),
        ...(data?.rejected_rules || [])
      ].sort((a, b) => a.position - b.position),
    [data]
  )

  // Jump to the first pending rule automatically when arriving from a "Review
  // Rules" action.
  useEffect(() => {
    if (!autoStartedRef.current && location.state?.startReview && (data?.pending_rules_count || 0) > 0) {
      autoStartedRef.current = true
      startReview()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, data])

  // And once extraction finishes (e.g. after an upload).
  const prevStatusRef = useRef(null)
  useEffect(() => {
    const status = data?.status
    if (prevStatusRef.current === 'extracting' && status === 'extracted' && (data?.pending_rules_count || 0) > 0) {
      startReview()
    }
    prevStatusRef.current = status
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.status, data?.pending_rules_count])

  // Clear the transient just-uploaded flag once the server reflects that
  // extraction has finished (or failed), so the status alert doesn't linger even
  // when the best-effort HTML render never produces rendered_html.
  useEffect(() => {
    if (data?.rendered_html || ['extracted', 'failed', 'completed'].includes(data?.status)) {
      setJustUploaded(false)
    }
  }, [data?.rendered_html, data?.status])

  // The HTML render runs after extraction and is best-effort: a Bedrock failure
  // leaves rendered_html null with status still "extracted". Bound how long we
  // show the "Preparing document view..." spinner so the rules never become
  // permanently unreachable; once it expires the view falls through (the document
  // panel itself handles missing HTML gracefully).
  const awaitingRenderedHtml =
    !!data?.has_document && data?.status === 'extracted' && !data?.rendered_html
  const [renderWaitExpired, setRenderWaitExpired] = useState(false)
  useEffect(() => {
    if (!awaitingRenderedHtml) {
      setRenderWaitExpired(false)
      return undefined
    }
    // Slightly longer than the refetch poll window (24 × 5s) so we only give up
    // after polling has actually stopped.
    const timer = setTimeout(() => setRenderWaitExpired(true), 130000)
    return () => clearTimeout(timer)
  }, [awaitingRenderedHtml])

  const availableSections = (sectionsData || [])
    .map((s) => ({ id: s.id, name: s.name }))
    .sort((a, b) => a.name.localeCompare(b.name))

  const handleSaveSections = (ids) => {
    updateSections(ids, { onSuccess: () => setShowSectionPicker(false) })
  }

  const toggleSection = (id) => {
    setSelectedSectionIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      handleSaveSections(next)
      return next
    })
  }

  const openSectionPicker = () => {
    setSelectedSectionIds((data?.sections || []).map((s) => s.id))
    setShowSectionPicker(true)
  }

  const handleAddRule = (e) => {
    e.preventDefault()
    if (!newCategory.trim() || !newContent.trim() || isCreatingRule) return
    createRule({
      category: newCategory.trim(),
      content: newContent.trim(),
      summary: newSummary.trim() || undefined,
      position: rules.length
    })
  }

  const handleDeleteRule = (ruleId) => {
    if (window.confirm('Are you sure you want to delete this rule?')) {
      deleteRule(ruleId)
    }
  }

  const handleRestore = (ruleId) => {
    updateRule({ ruleId, data: { status: 'pending' } })
  }

  if (isLoading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" />
      </div>
    )
  }

  if (error) {
    return <Alert variant="danger">Failed to load policy document.</Alert>
  }

  const rules = data?.rules || []
  const rejectedRules = data?.rejected_rules || []
  const showDocument = viewMode === 'document'
  // Section(s) chosen at the policy level — used to scope which analytics each
  // rule's "link analytics" search offers.
  const policySectionIds = (data?.sections || []).map((s) => s.id)

  // Whether this document has anything yet (an uploaded file or any rules).
  const hasContent = data?.has_document || rules.length > 0 || (data?.pending_rules_count || 0) > 0

  // While the document is being extracted or its HTML is still rendering, we show
  // a single status alert and hold back the rules until the view is ready.
  const documentPreparing =
    justUploaded ||
    data?.status === 'extracting' ||
    (awaitingRenderedHtml && !renderWaitExpired)
  const preparingText =
    data?.status === 'extracted'
      ? 'Preparing document view...'
      : 'Extracting rules from document. This may take a minute...'

  const addRuleForm = (
    <Card className="mb-2 border-primary">
      <Card.Header className="py-2 bg-white">
        <span className="fw-bold" style={{ fontSize: '0.85rem' }}>New Rule</span>
      </Card.Header>
      <Card.Body className="py-2">
        <Form onSubmit={handleAddRule}>
          <Form.Group className="mb-2">
            <Form.Label className="small fw-bold mb-1">Category</Form.Label>
            <Form.Control
              type="text"
              size="sm"
              placeholder="e.g. Spending Approvals"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              disabled={isCreatingRule}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label className="small fw-bold mb-1">Rule Content</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              size="sm"
              placeholder="Enter the full rule text..."
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              disabled={isCreatingRule}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label className="small fw-bold mb-1">Summary (optional)</Form.Label>
            <Form.Control
              type="text"
              size="sm"
              placeholder="Brief one-line summary"
              value={newSummary}
              onChange={(e) => setNewSummary(e.target.value)}
              disabled={isCreatingRule}
            />
          </Form.Group>
          <div className="d-flex gap-2">
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={isCreatingRule || !newCategory.trim() || !newContent.trim()}
            >
              {isCreatingRule ? <Spinner as="span" animation="border" size="sm" /> : 'Save Rule'}
            </Button>
            <Button
              variant="light"
              size="sm"
              onClick={() => { setShowAddForm(false); setNewCategory(''); setNewContent(''); setNewSummary('') }}
              disabled={isCreatingRule}
            >
              Cancel
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  )

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <Button variant="light" size="sm" onClick={onBack} className="me-2">
          <FontAwesomeIcon icon={faArrowLeft} />
        </Button>
        <span className="fw-bold">{data?.name}</span>
        <span className="text-muted ms-2 small">({rules.length} rules)</span>
        {data?.has_document && (
          <Button
            variant="link"
            size="sm"
            className="ms-2 p-0 small text-decoration-none"
            title={data.file_name}
            disabled={isDownloading}
            onClick={() => downloadDocument({ documentUrl: data.document_url, fileName: data.file_name })}
          >
            <FontAwesomeIcon icon={faFilePdf} className="me-1" />
            {isDownloading ? 'Downloading...' : 'Download PDF'}
          </Button>
        )}
        <div className="d-flex align-items-center gap-1 ms-2 position-relative" ref={sectionPickerRef}>
          <div
            className="d-flex flex-wrap gap-1 align-items-center border rounded px-2"
            style={{ minHeight: '28px', cursor: 'pointer', background: '#fff' }}
            onClick={openSectionPicker}
          >
            {(data?.sections || []).length === 0 ? (
              <span className="text-muted small">Add modules...</span>
            ) : (
              (data?.sections || []).map((s) => (
                <Badge key={s.id} bg="" className="badge-secondary" style={{ fontWeight: 'normal' }}>{s.name}</Badge>
              ))
            )}
            <FontAwesomeIcon icon={faPen} className="text-muted ms-1" style={{ fontSize: '0.65rem' }} />
          </div>
          {showSectionPicker && availableSections.length > 0 && (
            <div
              className="position-absolute bg-white border rounded shadow-sm"
              style={{ zIndex: 20, minWidth: '200px', maxHeight: '200px', overflowY: 'auto', top: '100%', left: 0 }}
            >
              {availableSections.map((s) => (
                <label
                  key={s.id}
                  className="d-flex align-items-center px-2 py-1 small gap-2"
                  style={{ cursor: 'pointer', whiteSpace: 'nowrap', margin: 0 }}
                >
                  <input
                    type="checkbox"
                    checked={selectedSectionIds.includes(s.id)}
                    onChange={() => toggleSection(s.id)}
                    disabled={isSavingSections}
                    style={{ cursor: 'pointer' }}
                  />
                  {s.name}
                </label>
              ))}
            </div>
          )}
        </div>
        {(data?.rendered_html || rules.length > 0 || (data?.pending_rules_count || 0) > 0) && (
          <div className="btn-group btn-group-sm ms-auto" role="group" aria-label="View mode">
            <Button
              variant={viewMode === 'document' ? 'primary' : 'light'}
              size="sm"
              className={viewMode === 'document' ? '' : 'text-primary'}
              onClick={() => setViewMode('document')}
              title="Document view"
            >
              <FontAwesomeIcon icon={faFileAlt} className="me-1" />Document
            </Button>
            <Button
              variant={viewMode === 'list' ? 'primary' : 'light'}
              size="sm"
              className={viewMode === 'list' ? '' : 'text-primary'}
              onClick={() => setViewMode('list')}
              title="List view"
            >
              <FontAwesomeIcon icon={faList} className="me-1" />List
            </Button>
          </div>
        )}
        {rejectedRules.length > 0 && (
          <Button
            variant="outline-danger"
            size="sm"
            className="ms-auto"
            style={{ fontSize: '0.75rem', padding: '0.15rem 0.4rem' }}
            onClick={() => setShowRejectedModal(true)}
          >
            Rejected ({rejectedRules.length})
          </Button>
        )}
      </div>

      {!hasContent ? (
        showAddForm ? (
          addRuleForm
        ) : (
          <div className="text-center py-5">
            <FontAwesomeIcon
              icon={aiEnabled ? faUpload : faPlus}
              className="text-muted mb-3"
              style={{ fontSize: '2rem' }}
            />
            <div className="fw-bold mb-2">{aiEnabled ? 'Upload a policy document' : 'Add your first rule'}</div>
            <p className="text-muted small mb-3 mx-auto" style={{ maxWidth: 460 }}>
              {aiEnabled
                ? 'Upload a PDF of this policy and we’ll automatically extract the rules for you to review and link to analytics.'
                : 'Create rules for this policy document to define what applies to your organization.'}
            </p>
            {aiEnabled ? (
              <Button variant="primary" onClick={() => setShowUploadModal(true)}>
                <FontAwesomeIcon icon={faUpload} className="me-1" />
                Upload &amp; Extract
              </Button>
            ) : (
              <Button variant="primary" onClick={() => setShowAddForm(true)}>
                <FontAwesomeIcon icon={faPlus} className="me-1" />
                Create Policy Rule
              </Button>
            )}
          </div>
        )
      ) : documentPreparing ? (
        <div className="d-flex align-items-center gap-2 p-3 bg-light rounded border mb-3">
          <Spinner animation="border" size="sm" variant="primary" />
          <span className="small text-muted">{preparingText}</span>
        </div>
      ) : (
        <>
          {data?.matching_status === 'matching' && (
            <div className="d-flex align-items-center gap-2 p-2 bg-light rounded border mb-3">
              <Spinner animation="border" size="sm" variant="primary" />
              <span className="small text-muted">Linking analytics to rules...</span>
            </div>
          )}

          {data?.matching_status === 'match_failed' && (
            <Alert variant="danger" className="d-flex align-items-center justify-content-between py-2 mb-3 small">
              <span>Analytics matching failed.</span>
              <Button
                variant="outline-danger"
                size="sm"
                disabled={isRetryingMatch}
                onClick={() => retryMatching(policyDocumentId)}
              >
                {isRetryingMatch ? 'Retrying...' : 'Retry linking'}
              </Button>
            </Alert>
          )}

          {(data?.pending_rules_count || 0) > 0 && (
            <Alert variant="warning" className="d-flex align-items-center justify-content-between py-2 mb-3">
              <span className="small">
                <FontAwesomeIcon icon={faClipboardCheck} className="me-2" />
                {data.pending_rules_count} rule{data.pending_rules_count === 1 ? '' : 's'} still need review. Finish reviewing the rules created from this document.
              </span>
              <Button variant="primary" size="sm" onClick={startReview}>
                Review Rules ({data.pending_rules_count})
              </Button>
            </Alert>
          )}

          {showDocument && (
            <PolicyDocumentContent
              html={data?.rendered_html}
              rules={allRules}
              reviewTrigger={reviewTrigger}
              onUpdateRule={updateRuleAsync}
              onBulkAccept={bulkAcceptAsync}
              onCreateRule={(payload) => createRuleAsync(payload).then((res) => res.rule)}
              onPersistHtml={persistHtmlAsync}
              onLinkAnalytic={linkAnalyticAsync}
              onUnlinkAnalytic={unlinkAnalytic}
              onRetryMatch={matchRuleAnalyticsAsync}
              onDeleteRule={deleteRuleAsync}
              allAnalytics={analyticsData?.data || []}
              policySectionIds={policySectionIds}
              municipalityId={user.municipality_id}
              composableSections={composableSections}
              defaultSectionId={defaultComposeSectionId}
              linking={data?.matching_status === 'matching'}
            />
          )}

          {!showDocument && (rules.length === 0 && !showAddForm ? (
            (data?.pending_rules_count || 0) > 0 ? null : (
              <p className="text-muted small">No rules saved. Click "Add Rule" to get started.</p>
            )
          ) : (
            rules.map((rule) => (
              <PolicyRuleCard
                key={rule.id}
                rule={rule}
                onUpdate={updateRule}
                onDelete={handleDeleteRule}
                onLinkAnalytic={linkAnalytic}
                onUnlinkAnalytic={unlinkAnalytic}
                allAnalytics={analyticsData?.data || []}
                policySectionIds={policySectionIds}
                municipalityId={user.municipality_id}
                composableSections={composableSections}
                defaultSectionId={defaultComposeSectionId}
              />
            ))
          ))}

          {!showDocument && (showAddForm ? addRuleForm : (
            <Button
              variant="outline-primary"
              size="sm"
              className="mt-2"
              onClick={() => setShowAddForm(true)}
            >
              <FontAwesomeIcon icon={faPlus} className="me-1" />
              Add Rule
            </Button>
          ))}
        </>
      )}

      {showUploadModal && data && (
        <PolicyUploadModal
          policyDocument={data}
          onHide={() => setShowUploadModal(false)}
          onSuccess={() => setJustUploaded(true)}
        />
      )}

      <Modal show={showRejectedModal} size="lg" centered onHide={() => setShowRejectedModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Rejected Rules ({rejectedRules.length})</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {rejectedRules.length === 0 ? (
            <p className="text-muted small mb-0">No rejected rules.</p>
          ) : (
            rejectedRules.map((rule) => (
              <Card key={rule.id} className="mb-2">
                <Card.Header className="py-2 d-flex align-items-center justify-content-between bg-white">
                  <span className="fw-bold" style={{ fontSize: '0.85rem' }}>{rule.category}</span>
                  <Button variant="outline-warning" size="sm" onClick={() => handleRestore(rule.id)}>
                    <FontAwesomeIcon icon={faUndo} className="me-1" />
                    Restore to Pending
                  </Button>
                </Card.Header>
                <Card.Body className="py-2">
                  {rule.summary && (
                    <p className="text-muted small fst-italic mb-2">{rule.summary}</p>
                  )}
                  <pre className="small mb-0" style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                    {rule.content}
                  </pre>
                </Card.Body>
              </Card>
            ))
          )}
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default PolicyItem
